import { Injectable } from '@angular/core';

/**
 * Gerencia o histórico de estados do editor para as funcionalidades de Desfazer (Undo) e Refazer (Redo).
 * Utiliza duas pilhas: uma para o histórico de ações (`historyStack`) e outra para as ações desfeitas (`redoStack`).
 */
@Injectable()
export class UndoRedoService {
  private historyStack: string[] = [];

  private redoStack: string[] = [];

  /**
   * Limita o número de estados mantidos no histórico para controlar o uso de memória.
   */
  private readonly MAX_HISTORY_SIZE = 50;

  /**
   * Salva o estado atual do editor na pilha de histórico.
   * - Se o novo estado for diferente do último estado salvo, ele é adicionado à `historyStack`.
   * - Qualquer novo estado limpa a `redoStack`, pois a linha do tempo foi alterada.
   *
   * @param state O estado do editor a ser salvo (geralmente o `innerHTML`).
   */
  public saveState(state: string): void {
    const lastState = this.historyStack[this.historyStack.length - 1];
    if (state !== lastState) {
      this.historyStack.push(state);
      // Uma nova ação invalida o histórico de "refazer".
      this.redoStack = [];
    }

    // Impede que a pilha de histórico cresça indefinidamente.
    if (this.historyStack.length > this.MAX_HISTORY_SIZE) {
      this.historyStack.shift(); // Remove o estado mais antigo.
    }
  }

  /**
   * Reverte para o estado anterior no histórico.
   * O estado atual é movido da `historyStack` para a `redoStack`.
   *
   * @returns A string do estado anterior, ou `null` se não houver histórico para desfazer.
   */
  public undo(): string | null {
    // Não pode desfazer o estado inicial.
    if (this.historyStack.length <= 1) {
      return null;
    }

    const currentState = this.historyStack.pop()!;
    this.redoStack.push(currentState);
    return this.historyStack[this.historyStack.length - 1];
  }

  /**
   * Aplica o próximo estado da pilha de "refazer".
   * O estado é movido da `redoStack` de volta para a `historyStack`.
   *
   * @returns A string do próximo estado, ou `null` se não houver estado para refazer.
   */
  public redo(): string | null {
    if (this.redoStack.length === 0) {
      return null;
    }

    const nextState = this.redoStack.pop()!;
    this.historyStack.push(nextState);
    return nextState;
  }
}
